import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, FileText, Loader2, AlertTriangle } from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import {
  downloadForm16,
  fetchForm16FinancialYears,
  parseTdsApiError,
} from "../../Moduels/TDS/tdsApi";
import { loadTdsConfig } from "../../Moduels/TDS/tdsStorage";

function resolveEmployeeId(user) {
  if (!user) return "";
  return (
    user?.employee?.employee_id ||
    user?.employee_id ||
    user?.user?.employee_id ||
    ""
  );
}

export default function Form16() {
  const { user } = useContext(AuthContext);
  const employeeId = resolveEmployeeId(user);
  const [financialYear, setFinancialYear] = useState("");
  const [fyOptions, setFyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchForm16FinancialYears()
      .then((data) => {
        setFinancialYear(data.current || "");
        setFyOptions(data.options || []);
      })
      .catch(() => {
        const y = new Date().getFullYear();
        const start = new Date().getMonth() >= 3 ? y : y - 1;
        const label = `${start}-${String(start + 1).slice(-2)}`;
        setFinancialYear(label);
        setFyOptions([label]);
      });
  }, []);

  const handleDownload = async () => {
    if (!employeeId) {
      setError("Your account is not linked to an employee profile.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const config = loadTdsConfig();
      const filename = await downloadForm16({
        employeeId,
        financialYear,
        signatory: config.form16,
      });
      setSuccess(`Downloaded ${filename}`);
    } catch (err) {
      setError(await parseTdsApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex justify-center items-start p-8">
      <motion.div
        className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-8"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-violet-100 text-violet-700">
            <FileText size={22} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Form 16</h2>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Download your tax deduction certificate (Part A, Part B, and month-wise TDS) from processed payroll.
        </p>

        {error ? (
          <div className="mb-4 flex gap-2 items-start text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        ) : null}

        {success ? (
          <div className="mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            {success}
          </div>
        ) : null}

        <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year</label>
        <select
          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 mb-6 focus:outline-none focus:ring-2 focus:ring-violet-400"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
        >
          {fyOptions.map((fy) => (
            <option key={fy} value={fy}>
              FY {fy}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleDownload}
          disabled={loading || !financialYear || !employeeId}
          className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl shadow-md transition"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
          {loading ? "Generating PDF…" : "Download Form 16"}
        </button>

        {!employeeId ? (
          <p className="text-xs text-amber-700 mt-4">
            Sign in with an employee-linked account to download your Form 16.
          </p>
        ) : null}
      </motion.div>
    </div>
  );
}
